import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Play, FileText, Mic, Check } from 'lucide-react';
import './LandingPage.css';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';

const LandingPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { user, showAuthModal } = useAuth();
  const [currentFeature, setCurrentFeature] = useState(0);
  const [pendingRedirect, setPendingRedirect] = useState(false);
  const [pendingState, setPendingState] = useState(null);
  const navigate = useNavigate();

  const [isPlaying1, setIsPlaying1] = useState(false);
  const [isPlaying2, setIsPlaying2] = useState(false);
  const videoRef1 = useRef(null);
  const videoRef2 = useRef(null);
  const howItWorksRef = useRef(null);

  const [heroText, setHeroText] = useState('');
  const [heroFile, setHeroFile] = useState(null);
  const [heroFileName, setHeroFileName] = useState('');
  const heroFileInputRef = useRef(null);
  const heroTextareaRef = useRef(null);

  // Live mic recording state
  const [isRecording, setIsRecording] = useState(false);
  const [micError, setMicError] = useState('');
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef('');

  const handleVideoClick1 = () => {
    if (videoRef1.current) { videoRef1.current.play(); setIsPlaying1(true); }
  };
  const handleVideoClick2 = () => {
    if (videoRef2.current) { videoRef2.current.play(); setIsPlaying2(true); }
  };
  const handleWatchDemoClick = () => {
    if (howItWorksRef.current) {
      howItWorksRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const plans = [
    {
      name: 'Free', tagline: 'Try AI-powered presentation creation', price: '$0',
      features: ['5 projects per month', 'Automated Video Synchronization', '2 Audio-Based Projects', '5 video exports (1080p)', 'Professional stock imagery'],
      support: 'Community support', cta: 'Start Free', ctaLink: '',
    },
    {
      name: 'Starter', tagline: 'For individual creators', price: '$12.99', priceInterval: '/month',
      features: ['70 projects per month', 'Automated Video Synchronization', '10 Audio-Based Projects', '20 video exports (1080p)', 'Priority processing queue'],
      support: 'Email support', cta: 'See All Features', ctaLink: '/pricing-plans',
    },
    {
      name: 'Creator', tagline: 'For educators and content professionals', price: '$29.99', priceInterval: '/month',
      features: ['200 projects per month', '50 Audio-Based Projects', '80 Video Exports (1080p)', 'Priority processing queue', 'Advanced template library'],
      support: 'Priority support', cta: 'See All Features', ctaLink: '/pricing-plans', highlight: false,
    },
    {
      name: 'Professional', tagline: 'For teams and course creators', price: '$59.99', priceInterval: '/month',
      features: ['350 projects per month', '100 Audio-Based Projects', '150 video exports (1080p)', 'Priority processing queue', 'Advanced template library'],
      support: 'Dedicated support', cta: 'See All Features', ctaLink: '/pricing-plans',
    },
  ];

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (user && pendingRedirect && pendingState) {
      if (pendingState.heroFile) {
        window.__cayotyHeroFile = pendingState.heroFile;
      }
      navigate('/generate', { state: pendingState });
      setPendingRedirect(false);
      setPendingState(null);
    }
  }, [user, pendingRedirect, pendingState, navigate]);

  const handleGetStartedClick = () => {
    if (user) { navigate('/generate'); }
    else { setPendingRedirect(true); showAuthModal('signup'); }
  };

  const handleHeroFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setHeroFile(file);
    setHeroFileName(file.name);
  };

  const handleRemoveHeroFile = () => {
    setHeroFile(null);
    setHeroFileName('');
    if (heroFileInputRef.current) heroFileInputRef.current.value = '';
  };

  const handleHeroSubmit = () => {
    // Use ref as fallback in case React state hasn't settled after speech recognition ends
    const text = heroText.trim() || finalTranscriptRef.current.trim();
    if (!text && !heroFile) return;
    const state = { heroPrompt: text || null, heroFile: heroFile || null, autoSubmit: true };
    if (heroFile) window.__cayotyHeroFile = heroFile;
    if (user) {
      navigate('/generate', { state });
    } else {
      setPendingState(state);
      setPendingRedirect(true);
      showAuthModal('signup');
    }
  };

  const handleMicClick = () => {
    if (isRecording) {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
      // Flush final transcript to state immediately on stop
      const final = finalTranscriptRef.current.trim();
      if (final) setHeroText(final);
      setIsRecording(false);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setMicError('Live mic not supported in this browser. Please use Chrome or Edge.');
      setTimeout(() => setMicError(''), 4000);
      return;
    }

    setMicError('');
    finalTranscriptRef.current = heroText;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => setIsRecording(true);

    recognition.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          finalTranscriptRef.current += e.results[i][0].transcript + ' ';
        } else {
          interim = e.results[i][0].transcript;
        }
      }
      setHeroText((finalTranscriptRef.current + interim).trimStart());
    };

    recognition.onerror = (e) => {
      if (e.error === 'not-allowed') {
        setMicError('Microphone access denied. Please allow mic access and try again.');
      } else if (e.error !== 'no-speech') {
        setMicError('Mic error. Please try again.');
      }
      setTimeout(() => setMicError(''), 4000);
      setIsRecording(false);
      recognitionRef.current = null;
    };

    recognition.onend = () => {
      // Flush any remaining transcript
      const final = finalTranscriptRef.current.trim();
      if (final) setHeroText(final);
      setIsRecording(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const handleHeroKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleHeroSubmit(); }
  };

  const canSubmitHero = heroText.trim().length > 0 || heroFile !== null;

  // Auto-resize textarea when speech recognition fills heroText
  useEffect(() => {
    if (heroTextareaRef.current) {
      heroTextareaRef.current.style.height = 'auto';
      heroTextareaRef.current.style.height = Math.min(heroTextareaRef.current.scrollHeight, 180) + 'px';
    }
  }, [heroText]);

  return (
    <div className="landing-page">

      {/* ── HERO ── */}
      <section className={`hero-section-network ${isVisible ? 'visible' : ''}`}>
        <div className="hero-network-container">

          {/* Left */}
          <div className="hero-content-left">

            <h1 className="hero-title">
              Turn Audio Into Video
              <span className="title-highlight"> Automatically.</span>
            </h1>

            {/* PROMPT BOX */}
            <div className="hero-prompt-box">
              <div className="hero-textarea-wrapper">
                <textarea
                  ref={heroTextareaRef}
                  className="hero-prompt-textarea"
                  placeholder={heroFile ? 'Optional: Add instructions for slide generation...' : 'Upload audio/video or describe your presentation...'}
                  value={heroText}
                  onChange={(e) => {
                    setHeroText(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 180) + 'px';
                  }}
                  onKeyDown={handleHeroKeyDown}
                  rows={1}
                  maxLength={heroFile ? 500 : 5000}
                />
                <div className="hero-textarea-actions">
                  {/* Left: mic + upload */}
                  <div className="hero-actions-left">
                    <button
                      className={`hero-mic-btn ${isRecording ? 'recording' : ''}`}
                      onClick={handleMicClick}
                      disabled={!!heroFile}
                      title={isRecording ? 'Stop recording' : 'Record with mic'}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 256 256">
                        <path d="M128,176a48.05,48.05,0,0,0,48-48V64a48,48,0,0,0-96,0v64A48.05,48.05,0,0,0,128,176ZM96,64a32,32,0,0,1,64,0v64a32,32,0,0,1-64,0Zm40,143.6V232a8,8,0,0,1-16,0V207.6A80.11,80.11,0,0,1,48,128a8,8,0,0,1,16,0,64,64,0,0,0,128,0,8,8,0,0,1,16,0A80.11,80.11,0,0,1,136,207.6Z"/>
                      </svg>
                    </button>
                    <button
                      className="hero-plus-btn"
                      onClick={() => heroFileInputRef.current?.click()}
                      disabled={!!heroFile || isRecording}
                      title="Upload Audio/Video"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 256 256">
                        <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"/>
                      </svg>
                    </button>
                  </div>
                  {/* Right: send */}
                  <button
                    className="hero-send-btn"
                    onClick={handleHeroSubmit}
                    disabled={!canSubmitHero || isRecording}
                    title="Generate"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 19V5M5 12l7-7 7 7"/>
                    </svg>
                  </button>
                </div>
              </div>
              {/* listening indicator */}
              {isRecording && (
                <div className="hero-listening-bar">
                  <span className="hero-listening-dot"></span>
                  <span className="hero-listening-dot"></span>
                  <span className="hero-listening-dot"></span>
                  <span className="hero-listening-text">Listening... click mic to stop</span>
                </div>
              )}
              {/* mic error */}
              {micError && (
                <div className="hero-mic-error">{micError}</div>
              )}
              {/* file chip — inside prompt-box, outside textarea-wrapper */}
              {heroFile && (
                <div className="hero-file-chip">
                  <span>{heroFile.type.startsWith('video/') ? '🎥' : '🎵'}</span>
                  <span className="hero-file-chip-name">{heroFileName}</span>
                  <button className="hero-file-chip-remove" onClick={handleRemoveHeroFile}>×</button>
                </div>
              )}
            </div>
            {/* END PROMPT BOX */}

            <p className="hero-subtitle">
              Upload audio → get slides synced to your audio in HD video. Or create slides from text prompts. Export as images, video, or PowerPoint.
            </p>

            <div className="hero-actions">
              <button className="btn-primary-landing" onClick={handleGetStartedClick}>
                <Sparkles className="btn-icon" />
                Try Cayoty free
              </button>
              <button className="btn-secondary" onClick={handleWatchDemoClick}>
                <Play className="btn-icon" />
                Watch demo
              </button>
            </div>

          </div>
          {/* end Left */}

          {/* Right — network visual */}
          <div className="hero-network-visual">
            <div className="network-section input-section">
              <div className="network-nodes">
                <div className="network-node input-node-1"><Mic size={20} /><span>Audio/Video</span></div>
                <div className="network-node input-node-2"><FileText size={20} /><span>Text Prompt</span></div>
              </div>
            </div>
            <svg className="vertical-connections" viewBox="0 0 400 200">
              <path d="M 100 0 Q 100 100, 200 200"/>
              <path d="M 300 0 Q 300 100, 200 200"/>
            </svg>
            <div className="network-hub">
              <div className="ai-engine-hub">
                <img src="/logo-s.png" alt="Cayoty AI" className="engine-logo"/>
                <div className="engine-pulse"></div>
                <div className="engine-pulse-2"></div>
              </div>
            </div>
            <svg className="vertical-connections-down" viewBox="0 0 400 200">
              <path d="M 200 0 Q 80 100, 80 200"/>
              <path d="M 200 0 Q 200 100, 200 200"/>
              <path d="M 200 0 Q 320 100, 320 200"/>
            </svg>
            <div className="network-section output-section">
              <div className="network-nodes">
                <div className="network-node output-node-1"><FileText size={20} /><span>PowerPoint</span></div>
                <div className="network-node output-node-2"><Play size={20} /><span>Videos</span></div>
                <div className="network-node output-node-3"><FileText size={20} /><span>Images</span></div>
              </div>
            </div>
          </div>
          {/* end Right */}

        </div>
      </section>
      {/* ── END HERO ── */}

      {/* Hidden file input */}
      <input
        ref={heroFileInputRef}
        type="file"
        accept="audio/*,video/*,.mp3,.mp4,.wav,.m4a,.webm,.mov,.avi"
        onChange={handleHeroFileChange}
        hidden
      />

      {/* ── HOW IT WORKS ── */}
      <div className="container-content" ref={howItWorksRef}>
        <h2 className="section-main-title">How Cayoty Works?</h2>

        <div className="content">
          <div className="left-section">
            <div className="feature-block">
              <div className="icon-wrapper">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                </svg>
              </div>
              <h2>Upload Audio Get Slides Video</h2>
              <p>Upload lecture recordings, Meeting recordings, podcasts. Cayoty generates corresponding slides based on the audio content, and produces a 1080p video with your audio perfectly synchronized to slide transitions. Ideal for online courses, explainer videos and training materials.</p>
            </div>
          </div>
          <div className="right-section">
            <div className="demo-video-container">
              <div className="video-wrapper">
                <video ref={videoRef1} className="demo-video" muted loop playsInline controls={isPlaying1}>
                  <source src="https://res.cloudinary.com/dbtuwe7us/video/upload/v1772164386/Cayoty_for_Gif_iy6obp.mp4" type="video/mp4"/>
                </video>
                {!isPlaying1 && (
                  <div className="play-button-overlay" onClick={handleVideoClick1}>
                    <div className="play-button">
                      <svg className="play-icon" viewBox="0 0 24 24" fill="#18181b"><path d="M8 5v14l11-7z"/></svg>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="content">
          <div className="left-section">
            <div className="feature-block">
              <div className="icon-wrapper">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3"/><path d="M12 1v6m0 6v6m9-9h-6m-6 0H3"/>
                </svg>
              </div>
              <h2>Turn Text into Slides Video</h2>
              <p>Describe your presentation in natural language. Cayoty's AI analyzes your input, structures the content, and generates professionally designed slides with layouts, formatting, and visual hierarchy. Export as editable PowerPoint files, static images, or presentation videos.</p>
            </div>
          </div>
          <div className="right-section">
            <div className="demo-video-container">
              <div className="video-wrapper">
                <video ref={videoRef2} className="demo-video" muted loop playsInline controls={isPlaying2}>
                  <source src="https://res.cloudinary.com/dbtuwe7us/video/upload/v1772164965/Cayoty_text_prompt_gif_q6uphy.mp4" type="video/mp4"/>
                </video>
                {!isPlaying2 && (
                  <div className="play-button-overlay" onClick={handleVideoClick2}>
                    <div className="play-button">
                      <svg className="play-icon" viewBox="0 0 24 24" fill="#18181b"><path d="M8 5v14l11-7z"/></svg>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── PROBLEMS ── */}
      <div className="problems-container-content">
        <h2 className="section-main-title">What Problem Does Cayoty Solves?</h2>
        <h2 className="section-main-subtitle">Create slides, videos, and exports without design skills or video editing</h2>
        <div className="problems-content-inner">
          <div className="problems-left-section">
            <div className="problems-network-container">
              <svg className="problems-connections" viewBox="0 0 800 800">
                <path d="M 400 400 Q 350 250, 300 150"/><path d="M 400 400 Q 300 320, 250 300"/>
                <path d="M 400 400 Q 280 400, 200 400"/><path d="M 400 400 Q 300 460, 250 500"/>
                <path d="M 400 400 Q 330 520, 300 600"/><path d="M 400 400 Q 460 250, 500 150"/>
                <path d="M 400 400 Q 520 280, 600 200"/><path d="M 400 400 Q 550 330, 650 300"/>
                <path d="M 400 400 Q 570 400, 680 400"/><path d="M 400 400 Q 550 470, 650 500"/>
                <path d="M 400 400 Q 520 520, 600 600"/><path d="M 400 400 Q 460 550, 500 650"/>
              </svg>
              <div className="problems-logo-center">
                <img src="/logo-land.png" alt="Cayoty Logo" className="problems-logo-image"/>
              </div>
              <div className="problems-node problems-node-1">Online Educators</div>
              <div className="problems-node problems-node-2">Course Creators</div>
              <div className="problems-node problems-node-3">Marketing Teams</div>
              <div className="problems-node problems-node-4">Content Creators</div>
              <div className="problems-node problems-node-5">Sales Teams</div>
              <div className="problems-node problems-node-6">Consultants</div>
              <div className="problems-node problems-node-7">Training Managers</div>
              <div className="problems-node problems-node-8">YouTubers</div>
              <div className="problems-node problems-node-9">Teachers</div>
              <div className="problems-node problems-node-10">Coaches</div>
              <div className="problems-node problems-node-11">Business Owners</div>
              <div className="problems-node problems-node-12">Podcasters</div>
              <div className="problems-node problems-node-13">Agencies</div>
              <div className="problems-node problems-node-14">Nonprofits</div>
              <div className="problems-node problems-node-15">Researchers</div>
              <div className="problems-node problems-node-16">Startups</div>
              <div className="problems-node problems-node-17">HR Teams</div>
              <div className="problems-node problems-node-18">Product Managers</div>
              <div className="problems-node problems-node-19">Freelancers</div>
              <div className="problems-node problems-node-20">Event Speakers</div>
              <div className="problems-node problems-node-21">Influencers</div>
            </div>
          </div>
          <div className="problems-right-section">
            <div className="problems-feature-block">
              <div className="problems-icon-wrapper">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/>
                  <line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/>
                  <line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/>
                  <line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="17" x2="22" y2="17"/>
                  <line x1="17" y1="7" x2="22" y2="7"/>
                </svg>
              </div>
              <h2>Transform audio into production-ready videos</h2>
              <p>Content creation shouldn't require juggling multiple tools and hours of manual work. The traditional workflow—building slides, recording audio, then syncing everything in a video editor—drains time and creative energy. Cayoty eliminates this entirely. Upload your audio, and receive a professionally produced 1080p video with intelligently timed transitions that flow naturally with your narration.</p>
            </div>
            <div className="problems-feature-block">
              <div className="problems-icon-wrapper">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
                </svg>
              </div>
              <h2>Generate presentations in minutes</h2>
              <p>Design shouldn't be a bottleneck to communication. Provide a text prompt, and Cayoty generates multiple professionally designed options instantly. Review the variations, select your preferred design, and refine with your own images if needed. No template hunting, no layout adjustments, no starting from a blank canvas—just curated choices that save you hours of work.</p>
            </div>
            <div className="problems-feature-block">
              <div className="problems-icon-wrapper">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/>
                </svg>
              </div>
              <h2>One Creation. Multiple Exports.</h2>
              <p>Export a single creation as an editable PowerPoint for collaboration, broadcast-quality video for learning and presentation platforms, or optimized images for social distribution. Create once, publish everywhere—without friction, rework, or duplicated effort.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── PRICING ── */}
      <section className="pricing-section-landing">
        <h2 className="pricing-title-landing">Simple, Transparent Pricing.</h2>
        <p className="pricing-subtitle-landing">Pay only for what you need. Choose the plan that fits your needs</p>
        <div className="pricing-grid-landing">
          {plans.map((plan, i) => (
            <div key={i} className={`pricing-card-landing ${plan.highlight ? 'highlighted-landing' : ''}`}>
              {plan.popular && <div className="popular-badge-landing">Most Popular</div>}
              <h3 className="plan-name-landing">{plan.name}</h3>
              <p className="plan-tagline-landing">{plan.tagline}</p>
              <div className="plan-price-container-landing">
                <p className="plan-price-landing">
                  {plan.price}
                  {plan.priceInterval && <span className="price-interval-landing">{plan.priceInterval}</span>}
                </p>
              </div>
              <ul className="plan-features-landing">
                {plan.features.map((f, j) => (
                  <li key={j} className="plan-feature-item-landing">
                    <div className="plan-check-icon-landing"><Check size={12} color="black" strokeWidth={3}/></div>
                    <span className="plan-feature-text-landing">{f}</span>
                  </li>
                ))}
              </ul>
              <p className="plan-support-landing">Support: {plan.support}</p>
              <button className="plan-cta-landing" onClick={() => { navigate(plan.ctaLink); window.scrollTo(0, 0); }}>
                {plan.cta}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="faq-section-landing">
        <h2 className="faq-title-landing">Frequently Asked Questions</h2>
        <p className="faq-subtitle-landing">Everything you need to know about Cayoty</p>
        <div className="faq-grid-landing">
          <div className="faq-item-landing"><h3 className="faq-question-landing">What is Cayoty?</h3><p className="faq-answer-landing">Cayoty is an AI-powered platform that transforms text prompts or audio recordings into professional presentations. Generate slides from descriptions or create synchronized video presentations from audio files.</p></div>
          <div className="faq-item-landing"><h3 className="faq-question-landing">Do I need design skills?</h3><p className="faq-answer-landing">No! Cayoty automatically handles layouts, formatting, visual hierarchy, and adds professional stock imagery. Just provide your content and get polished slides.</p></div>
          <div className="faq-item-landing"><h3 className="faq-question-landing">How do the two modes work?</h3><p className="faq-answer-landing">Prompt to Slide: Describe your presentation in text (30-60 seconds). Audio to Slide: Upload audio files and get slides with synchronized 1080p video output (1-2 minutes).</p></div>
          <div className="faq-item-landing"><h3 className="faq-question-landing">What audio/video formats are supported?</h3><p className="faq-answer-landing">MP3, WAV, and MP4, etc files up to 100MB. Audio length limits vary by plan: Free/Starter (10 min), Creator (40 min), Professional (60 min).</p></div>
          <div className="faq-item-landing"><h3 className="faq-question-landing">What export formats are available?</h3><p className="faq-answer-landing">Export as PowerPoint (PPTX), PNG, or HD video. PPTX and PNG exports are unlimited on all plans. Video exports include automatic transitions and timing or duration selection.</p></div>
          <div className="faq-item-landing"><h3 className="faq-question-landing">Can I edit the generated slides?</h3><p className="faq-answer-landing">You can upload images and select the designs directly in the platform before exporting. Export as PPTX for full editing of texts in PowerPoint.</p></div>
          <div className="faq-item-landing"><h3 className="faq-question-landing">How many presentations do I get?</h3><p className="faq-answer-landing">Free: 5/month, Starter: 70/month, Creator: 200/month, Professional: 350/month. Check your Sidebar for current balance and usage.</p></div>
          <div className="faq-item-landing"><h3 className="faq-question-landing">How do I write effective prompts?</h3><p className="faq-answer-landing">Be specific! Example: "Create a 10-slide pitch deck about renewable energy for investors" works better than "slides about solar".</p></div>
          <div className="faq-item-landing"><h3 className="faq-question-landing">Can I paste existing content?</h3><p className="faq-answer-landing">Absolutely! Paste articles, notes, or outlines into the prompt. Cayoty will analyze and structure your content into a formatted presentation with proper slide breaks.</p></div>
          <div className="faq-item-landing"><h3 className="faq-question-landing">Are my presentations saved?</h3><p className="faq-answer-landing">Yes! All generations are automatically saved in your dashboard history. Access "My Presentations" anytime to view and download previous creations.</p></div>
          <div className="faq-item-landing"><h3 className="faq-question-landing">HD Video Generation taking too long?</h3><p className="faq-answer-landing">Video sync generation takes time according to your video length. If it's a 5-minute audio, it will take between 5 to 10 minutes to sync and generate HD video.</p></div>
          <div className="faq-item-landing"><h3 className="faq-question-landing">Need help or have questions?</h3><p className="faq-answer-landing">Contact our support team at Support@cayoty.com or use the chat widget. We typically respond within 1 hour during business hours.</p></div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="cta-landing">
        <div className="cta-container-landing">
          <h2 className="cta-title-landing">Ready to transform your content creation?</h2>
          <p className="cta-subtitle-landing">Join the creators producing professional videos or presentations in seconds, not hours.</p>
          <button className="cta-button-landing" onClick={handleGetStartedClick}>Try Cayoty free</button>
          <p className="cta-note-landing">No credit card required · 5 free projects to start</p>
        </div>
      </section>

    </div>
  );
};

export default LandingPage;